import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { cfmt, pfmt } from '../../lib/api';

/**
 * Full ranked results — post-signup. All 10 matches with reasons and concerns.
 */
export default function MatchResults({ results }) {
    if (!results || !results.length) return null;

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20" data-testid="match-results">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data flex items-center gap-3">
                    <span className="inline-block w-6 h-px bg-neutral-400" />
                    Your full match report
                </div>
                <h2 className="font-editorial mt-6 leading-[1.03]" style={{ fontSize: 'clamp(36px, 4.6vw, 60px)', letterSpacing: '-0.02em' }}>
                    Ten neighbourhoods. <span className="italic text-neutral-500">Ranked, honestly.</span>
                </h2>
                <p className="mt-5 font-editorial text-neutral-600 max-w-2xl" style={{ fontSize: '18px', lineHeight: 1.55 }}>
                    Every score below is derived from your answers — no filler, no upsell. Click any neighbourhood to open its full profile.
                </p>
            </motion.div>

            <div className="mt-12 space-y-4">
                {results.map((r, i) => (
                    <motion.div
                        key={r.slug}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 0.61, 0.36, 1] }}
                        className="border border-neutral-200 bg-white hover:border-brand-navy transition-colors"
                        data-testid={`match-result-${i}`}
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                            {/* left — rank + name + %  */}
                            <div className="lg:col-span-4 p-6 lg:p-8 border-r border-neutral-200">
                                <div className="flex items-baseline justify-between">
                                    <div className="font-mono-data text-[12px] text-neutral-400 tabular">
                                        RANK {String(i + 1).padStart(2, '0')}
                                    </div>
                                    <div className="text-right">
                                        <div className="font-editorial tabular text-black" style={{ fontSize: '40px', lineHeight: 1, letterSpacing: '-0.02em' }}>
                                            {r.match_percentage}
                                            <span className="text-[20px] text-neutral-400">%</span>
                                        </div>
                                    </div>
                                </div>
                                <Link to={`/neighbourhoods/${r.slug}`} className="block mt-6 group">
                                    <div className="font-editorial tracking-editorial leading-tight group-hover:italic" style={{ fontSize: '30px', letterSpacing: '-0.01em' }}>
                                        {r.name}
                                    </div>
                                    <div className="mt-1.5 text-[12px] tracking-[0.18em] uppercase text-neutral-500 font-mono-data">
                                        {r.city} · {r.region}
                                    </div>
                                </Link>
                                <div className="mt-6 flex items-center gap-4 text-[12px] font-mono-data text-neutral-500">
                                    <span>Median <span className="text-black">{cfmt(r.median_price)}</span></span>
                                    <span>+{pfmt(r.forecast_5yr_pct)} 5Y</span>
                                </div>
                            </div>

                            {/* middle — reasons */}
                            <div className="lg:col-span-6 p-6 lg:p-8 border-r border-neutral-200">
                                <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 font-mono-data">Why it fits</div>
                                <ul className="mt-4 space-y-2.5">
                                    {r.reasons.map((reason, ri) => (
                                        <li key={ri} className="text-[13.5px] text-black leading-relaxed flex gap-3">
                                            <span className="font-mono-data text-neutral-400 tabular shrink-0">0{ri + 1}</span>
                                            <span>{reason}</span>
                                        </li>
                                    ))}
                                </ul>
                                {r.concern && (
                                    <div className="mt-5 pt-4 border-t border-neutral-200">
                                        <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500 font-mono-data">Trade-off</div>
                                        <p className="mt-1.5 text-[12.5px] text-neutral-700 leading-relaxed">{r.concern}</p>
                                    </div>
                                )}
                            </div>

                            {/* right — arrow / explore */}
                            <Link to={`/neighbourhoods/${r.slug}`} className="lg:col-span-2 p-6 lg:p-8 flex items-center justify-center bg-white hover:bg-brand-navy hover:text-white transition-colors group">
                                <div className="text-center">
                                    <ArrowUpRight className="w-6 h-6 mx-auto text-neutral-400 group-hover:text-white transition-colors" />
                                    <div className="mt-3 text-[11px] tracking-[0.22em] uppercase font-mono-data">
                                        Explore
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </motion.div>
                ))}
            </div>

            <div className="mt-16 pt-10 border-t border-neutral-200 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                <div className="max-w-xl">
                    <div className="overline">Next step</div>
                    <p className="mt-4 font-editorial italic text-neutral-700 leading-snug" style={{ fontSize: '20px' }}>
                        &ldquo;A neighbourhood is a hundred small decisions. Now the shortlist is honest — go visit.&rdquo;
                    </p>
                </div>
                <Link
                    to="/strategy-session"
                    className="inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3.5 text-[13.5px] font-medium hover:bg-neutral-800"
                   
                    data-testid="match-results-strategy-cta"
                >
                    Book a strategy session <ArrowUpRight className="w-4 h-4" />
                </Link>
            </div>
        </div>
    );
}
